import { useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react'
import { CreateClient } from '../../graphql';
import SuccessAlert from '../../components/Alerts/SuccessAlert';
import FailAlert from '../../components/Alerts/FailAlert';
import useStore from '../../store/useStore';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom'
import LoadingWidget from '../../components/LoadingWidget/LoadingWidget';
import PermissionsError from '../../components/Alerts/PermissionsError';

const AddClient = () => {
  const storeData = useStore((state: any) => state.userData.store);
  const userData = useStore((state: any) => state.userData);

  const [permissions, setPermissions] = useState({
    create: false,
  })

  // i had to use a main state for control loading because of ui issues
  const [initialLoading, setInitialLoading] = useState(true);

  // Permission configuration
  useEffect(() => {
    if (Object.keys(userData).length !== 0) {
      // admin
      if (userData.permissions.length === 0 && userData.role === "admin") {
        setPermissions({
          create: true,
        })

        setInitialLoading(false)
        return
      }

      const filtered = userData?.permissions?.filter((perm: { name: string }) => perm.name === "clients")

      let createPermission = filtered[0]?.permissions.includes("create")

      setPermissions({
        create: createPermission || false,
      })

      setInitialLoading(false)
    }
  }, [userData])

  const navigate = useNavigate();

  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [fail, setFail] = useState(false);
  const [failMsg, setFailMsg] = useState("");

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [date, setDate] = useState('');
  const [city, setCity] = useState('');
  const [notice, setNotice] = useState('');
  const [gander, setGander] = useState('male');

  // Create
  const [createClient, { error, loading }] = useMutation(CreateClient);

  const submit = async (e: any) => {
    e.preventDefault()

    if (name === "") {
      setFailMsg(t('emptyClientNameAlert'));
      setFail(true);
      return
    }

    if (email === "") {
      setFailMsg(t('emptyEmailAlert'));
      setFail(true);
      return

    } else {
      // Email validation regex pattern
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(email)) {
        setFailMsg(t('invalidEmailAlert'));
        setFail(true);
        return

      }
    }
    if (phone === "") {
      setFailMsg(t('emptyPhoneAlert'));
      setFail(true);
      return
    }
    if (date === "") {
      setFailMsg(t('emptyDateOfBirthAlert'));
      setFail(true);
      return
    }
    if (city === "") {
      setFailMsg(t('emptyCityAlert'));
      setFail(true);
      return
    }

    await createClient({
      variables: {
        content: {
          address: city,
          dateBirth: date,
          email: email,
          gander: gander,
          name: name,
          note: notice,
          phone: phone,
          idStore: storeData.id
        }
      }
    })

    if (!error) {
      setSuccessMsg(t('clientAddSuccess'));
      setSuccess(true);

      // CLear the form
      setName("");
      setCity("");
      setDate("");
      setEmail("");
      setNotice("");
      setPhone("");

      window.setTimeout(() => {
        navigate(`/clients`);
      }, 1000);
    }
  }

  return (
    <section className='my-4 mx-3 md:mx-7'>
      {success && <SuccessAlert success={success} setSuccess={setSuccess} successMsg={successMsg} />}

      {fail && <FailAlert fail={fail} setFail={setFail} failMsg={failMsg} />}

      <div className='flex flex-col-reverse md:flex-row md:justify-between md:items-center'>
        <div>
          <h3 className='text-2xl font-semibold'>{t('clients')}</h3>
        </div>
        <div>
          <Breadcrumbs />
        </div>
      </div>

      {
        initialLoading ?
          <LoadingWidget />
          :
          permissions.create === false ?
            <PermissionsError />
            :
            <form className='relative px-5 py-2 my-2 bg-white rounded-md' onSubmit={(e) => e.preventDefault}>
              {
                loading &&
                <div className='absolute left-0 z-30 flex items-center justify-center w-full h-full py-10 bg-white/70'>
                  <svg aria-hidden="true" className="w-8 h-8 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                  </svg>
                </div>
              }

              <div className='grid gap-4 mb-2 grid-flow-col-1 sm:grid-cols-2'>
                <div>
                  <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-900">{t('name')}</label>
                  <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('name')} />
                </div>
                <div>
                  <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900">{t('email')}</label>
                  <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('email')} />
                </div>
              </div>

              <div className='grid gap-4 mb-2 grid-flow-col-1 sm:grid-cols-2'>
                <div>
                  <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-900">{t('phone')}</label>
                  <input type="text" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('phone')} />
                </div>
                <div>
                  <label htmlFor="dateOfBirth" className="block mb-2 text-sm font-medium text-gray-900">{t('dateOfBirth')}</label>
                  <input type="date" id="dateOfBirth" value={date} onChange={(e) => setDate(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('dateOfBirth')} />
                </div>
              </div>

              <div className='grid gap-4 mb-2 grid-flow-col-1 sm:grid-cols-2'>
                <div>
                  <label htmlFor="city" className="block mb-2 text-sm font-medium text-gray-900">{t('city')}</label>
                  <input type="text" id="city" value={city} onChange={(e) => setCity(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('city')} />
                </div>
                <div>
                  <label htmlFor="gander" className="block mb-2 text-sm font-medium text-gray-900">{t('gander')}</label>
                  <select value={gander} id="gander" onChange={(e) => setGander(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 outline-none">
                    <option value="male">{t('male')}</option>
                    <option value="female">{t('female')}</option>
                  </select>
                </div>
              </div>

              <div>
                <div className="mb-2">
                  <label htmlFor="notice" className="block mb-2 text-sm font-medium text-gray-900">{t('notices')}</label>
                  <textarea id="notice" value={notice} onChange={(e) => setNotice(e.target.value)} className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary focus:border-primary outline-none block w-full p-2.5" placeholder={t('notices')} />
                </div>
              </div>


              <div className='flex justify-end pt-5'>
                <button onClick={(e) => submit(e)} className='inline-flex items-center justify-center w-full px-4 py-2.5 overflow-hidden text-sm text-white transition-colors duration-300 bg-primary rounded-lg shadow sm:w-auto my-2 md:my-0 hover:bg-primaryHover'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 2H8c-1.103 0-2 .897-2 2v12c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2V4c0-1.103-.897-2-2-2zM8 16V4h12l.002 12H8z"></path>
                    <path d="M4 8H2v12c0 1.103.897 2 2 2h12v-2H4V8zm11-2h-2v3h-3v2h3v3h2v-3h3V9h-3z"></path>
                  </svg>

                  <span className="mx-2">
                    {t('addClient')}
                  </span>
                </button>
              </div>
            </form>
      }
    </section >
  )
}

export default AddClient